import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SectionFrame } from "../atoms/SectionFrame";
import { TID } from "../../lib/testIds";

const STAGES = [
    "Discover",
    "Strategy",
    "Script",
    "Storyboard",
    "Design",
    "Motion",
    "Review",
    "Delivery",
];

export function ProcessSection() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 0.85", "end 0.15"],
    });
    const lineScaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
    const lineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

    return (
        <SectionFrame innerClassName="">
            <div ref={ref} className="py-20 md:py-32 px-6 md:px-12">
                <div className="mb-12 md:mb-16">
                    <div className="text-base uppercase tracking-[0.3em] mb-4"
                         style={{ color: "var(--accent)" }}>
                        PROCESS
                    </div>
                    <h3
                        className="display-text font-black uppercase"
                        style={{
                            fontSize: "clamp(2rem, 6vw, 4.5rem)",
                            lineHeight: 1,
                            letterSpacing: "-0.03em",
                        }}
                    >
                        Eight stages.<br />One unbroken line.
                    </h3>
                </div>

                {/* Desktop horizontal line */}
                <div className="hidden md:block relative py-24">
                    <div className="absolute left-0 right-0 top-1/2 h-[2px]"
                         style={{ background: "var(--line)", transform: "translateY(-1px)" }}>
                        <motion.div
                            className="absolute top-0 left-0 h-full"
                            style={{
                                background: "linear-gradient(90deg, var(--accent), var(--accent))",
                                boxShadow: "0 0 12px var(--accent-soft), 0 0 32px var(--accent-soft)",
                                scaleX: lineScaleX,
                                transformOrigin: "left center",
                                width: "100%",
                            }}
                        />
                    </div>
                    <div className="relative grid grid-cols-8 gap-4">
                        {STAGES.map((s, i) => (
                            <ProcessStage key={s} label={s} index={i} progress={scrollYProgress} total={STAGES.length} alternate />
                        ))}
                    </div>
                </div>

                {/* Mobile vertical line */}
                <div className="md:hidden relative pl-10">
                    <div className="absolute left-4 top-0 bottom-0 w-[2px]"
                         style={{ background: "var(--line)" }}>
                        <motion.div
                            className="absolute top-0 left-0 w-full"
                            style={{
                                background: "linear-gradient(180deg, var(--accent), var(--accent))",
                                boxShadow: "0 0 12px var(--accent-soft), 0 0 32px var(--accent-soft)",
                                scaleY: lineScaleY,
                                transformOrigin: "top center",
                                height: "100%",
                            }}
                        />
                    </div>
                    <div className="space-y-7">
                        {STAGES.map((s, i) => (
                            <ProcessStage key={s} label={s} index={i} progress={scrollYProgress} total={STAGES.length} />
                        ))}
                    </div>
                </div>
            </div>
        </SectionFrame>
    );
}

function ProcessStage({ label, index, progress, total, alternate = false }) {
    const start = index / total;
    const end = (index + 1) / total;
    const color = useTransform(progress, [start, end], ["#8A8F98", "#ECECEC"]);
    const dotScale = useTransform(progress, [start - 0.05, start + 0.05], [0.8, 1.4]);
    const dotGlow = useTransform(progress, [start - 0.05, start + 0.05],
        ["0 0 0px transparent", "0 0 18px var(--accent)"]);
    const slug = label.toLowerCase();

    if (alternate) {
        const above = index % 2 === 0;
        return (
            <div className="relative flex flex-col items-center" data-testid={TID.processStage(slug)}>
                {above && (
                    <motion.div style={{ color }} className="text-center mb-12 font-medium uppercase tracking-wide"
                                >
                        <div className="text-sm tracking-[0.25em]" style={{ color: "var(--muted)" }}>0{index + 1}</div>
                        <div style={{ fontSize: "clamp(0.9rem, 1.5vw, 1.2rem)" }}>{label}</div>
                    </motion.div>
                )}
                {!above && <div className="mb-12 invisible">spacer</div>}

                <motion.div
                    className="relative w-3 h-3 rounded-full"
                    style={{
                        background: "var(--accent)",
                        scale: dotScale,
                        boxShadow: dotGlow,
                    }}
                />

                {!above && (
                    <motion.div style={{ color }} className="text-center mt-12 font-medium uppercase tracking-wide">
                        <div className="text-sm tracking-[0.25em]" style={{ color: "var(--muted)" }}>0{index + 1}</div>
                        <div style={{ fontSize: "clamp(0.9rem, 1.5vw, 1.2rem)" }}>{label}</div>
                    </motion.div>
                )}
                {above && <div className="mt-12 invisible">spacer</div>}
            </div>
        );
    }

    // mobile vertical
    return (
        <div className="relative flex items-center gap-4" data-testid={TID.processStage(slug)}>
            <motion.div
                className="absolute -left-[24px] w-3 h-3 rounded-full"
                style={{
                    background: "var(--accent)",
                    scale: dotScale,
                    boxShadow: dotGlow,
                    top: "50%",
                    transform: "translateY(-50%)",
                }}
            />
            <motion.div style={{ color }} className="font-medium uppercase tracking-wide">
                <div className="text-sm tracking-[0.25em]" style={{ color: "var(--muted)" }}>0{index + 1}</div>
                <div style={{ fontSize: "clamp(1rem, 4vw, 1.4rem)" }}>{label}</div>
            </motion.div>
        </div>
    );
}
